import PageLoader from '@components/PageLoader'
import { useAdminEvent } from '@hooks/useAdminEvent'
import React from 'react'
import { useParams } from 'react-router-dom'
import { formatDateTime, formatName } from '../adminFormat'
import {
	BackLink,
	Card,
	CardTitle,
	Chips,
	DeletedBadge,
	DetailHeader,
	DetailTitle,
	EmptyState,
	ErrorState,
	Field,
	FieldGrid,
	FieldLabel,
	FieldValue,
	InlineLink,
	Tag,
} from '../adminDetail.styles'

/**
 * Read-only admin detail for one itinerary event: its trip (linked), creator
 * (linked), schedule, location, tags, and timestamps. No mutations.
 */
const EventDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>()
	const { data: event, isLoading, isError } = useAdminEvent(Number(id))

	return (
		<>
			<BackLink to="/admin/events">← Back to events</BackLink>

			{isLoading ? (
				<PageLoader label="Loading event…" />
			) : isError || !event ? (
				<ErrorState>Could not load this event.</ErrorState>
			) : (
				<>
					<DetailHeader>
						<DetailTitle>{event.name}</DetailTitle>
						{event.isDeleted && <DeletedBadge>Deleted</DeletedBadge>}
					</DetailHeader>

					<Card>
						<CardTitle>Event</CardTitle>
						<FieldGrid>
							<Field>
								<FieldLabel>Trip</FieldLabel>
								<FieldValue>
									<InlineLink to={`/admin/trips/${event.tripId}`}>{event.tripName}</InlineLink>
								</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Day</FieldLabel>
								<FieldValue>Day {event.tripDayIndex + 1}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Start time</FieldLabel>
								<FieldValue>{event.startTime ?? '—'}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Category</FieldLabel>
								<FieldValue>{event.category}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Location</FieldLabel>
								<FieldValue>{event.locationName ?? '—'}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Created by</FieldLabel>
								<FieldValue>
									<InlineLink to={`/admin/users/${event.createdBy.id}`}>
										{formatName(event.createdBy.firstName, event.createdBy.lastName)}
									</InlineLink>
								</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Created</FieldLabel>
								<FieldValue>{formatDateTime(event.createdAt)}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Last updated</FieldLabel>
								<FieldValue>{formatDateTime(event.lastUpdatedAt)}</FieldValue>
							</Field>
						</FieldGrid>
					</Card>

					{event.description && (
						<Card>
							<CardTitle>Description</CardTitle>
							<FieldValue>{event.description}</FieldValue>
						</Card>
					)}

					<Card>
						<CardTitle>Tags ({event.tags.length})</CardTitle>
						{event.tags.length === 0 ? (
							<EmptyState>No tags.</EmptyState>
						) : (
							<Chips>
								{event.tags.map(tag => (
									<Tag key={tag}>{tag}</Tag>
								))}
							</Chips>
						)}
					</Card>
				</>
			)}
		</>
	)
}

export default EventDetailPage
